import { memo } from "react";
import type { ClosedInspectionRegister } from "../../InspectionsLists.d";
import { useDate } from "@/hooks/use-date/useDate";
import circleArrow from "@/assets/images/inspections-table/circleArrow.svg";
import downloaDocument from "@/assets/images/inspections-table/downloadDocument.svg";
import { Link } from "react-router-dom";
import { BASE_URL } from "@/assets/constants/constants";
import { getInspectionPDF } from "@/lib/services/inspections";
import { useLoader } from "@/context/loader-context/LoaderContext";
import { appToast } from "@/lib/toast";
import { useTranslate } from "@/hooks/use-translate/useTranslate";

export interface ClosedInspectionsProps {
  tableData: ClosedInspectionRegister[];
}

const ClosedInspections = ({ tableData }: ClosedInspectionsProps) => {
  const { formatDate, getTotalDays } = useDate();
  const { showLoader, hideLoader } = useLoader();
  const translate = useTranslate();

  const downloadPdf = async (id: number) => {
    try {
      showLoader();
      const response = await getInspectionPDF({ id });

      const url = window.URL.createObjectURL(
        new Blob([response], { type: "application/pdf" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `inspection_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      appToast.success(translate("success"), translate("pdfDownloadSuccess"));
    } catch (error) {
      const e = error as Error;

      appToast.error(translate("error"), e.message);
    } finally {
      hideLoader();
    }
  };

  return tableData?.map((register: ClosedInspectionRegister, index: number) => (
    <tr
      data-testid="closed-inspection-row"
      key={`tr-${index}`}
      className={`${index % 2 === 0 ? "table-odd-row" : "table-even-row"}${
        index === tableData.length - 1 ? " last-row" : ""
      } h-16 border-b border-b-ib-gray-lightest text-[14px] leading-[16px] text-ib-gray-darkest tracking-neg-1`}
    >
      <td className="h-full left-cell"></td>
      <td className="p-3 text-center">
        <span>{formatDate(register.createdAt)}</span>
      </td>

      <td className="h-16 p-3 flex flex-row justify-center gap-x-2 items-center">
        <span>{formatDate(register.endDate)}</span>
        <span className="font-ib-bold">
          {getTotalDays(register.createdAt, register.endDate)}{" "}
        </span>
      </td>
      <td className="p-3 text-center">{register.engineName}</td>
      <td className="p-3 text-center">{register.snStand}</td>
      <td className="p-3 text-center">{register.esnOut}</td>
      <td className="p-3 text-center">{register.colour}</td>
      <td className="px-3 py-2 right-cell">
        <div className="w- full flex flex-row justify-end items-center gap-x-6">
          <button
            data-testid="pdf-button"
            value={register.id}
            onClick={() => downloadPdf(register.id)}
            onTouchStart={() => downloadPdf(register.id)}
          >
            <img className="h-[20px] min-w-[20px]" src={downloaDocument} />
          </button>
          <Link to={`${BASE_URL}/${register.id}`}>
            <button data-testid="open-inspection-button" value={register.id}>
              <img className="h-[20px] min-w-[20px]" src={circleArrow} />
            </button>
          </Link>
        </div>
      </td>
    </tr>
  ));
};

export default memo(ClosedInspections);
